import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { X } from "lucide-react";

const COPY = {
  de: {
    body: "Wir verwenden technisch notwendige Cookies und – nach deiner Zustimmung – Cookies für Analyse und Marketing. Details in unserer",
    link: "Datenschutzerklärung",
    accept: "Alle akzeptieren",
    essential: "Nur notwendige",
  },
  en: {
    body: "We use technically required cookies and – with your consent – cookies for analytics and marketing. Details in our",
    link: "Privacy Policy",
    accept: "Accept all",
    essential: "Essential only",
  },
  it: {
    body: "Utilizziamo cookies tecnici necessari e – con il tuo consenso – cookies per analisi e marketing. Dettagli nella nostra",
    link: "Informativa sulla Privacy",
    accept: "Accetta tutto",
    essential: "Solo necessari",
  },
  ja: {
    body: "技術的に必要なCookieに加え、ご同意いただいた場合のみ分析・マーケティングCookieを使用します。詳細は",
    link: "プライバシーポリシー",
    accept: "すべて同意",
    essential: "必要なもののみ",
  },
  zh: {
    body: "我们使用技术必需的Cookie,并在您同意后使用分析和营销Cookie。详情请见",
    link: "隐私政策",
    accept: "全部接受",
    essential: "仅必需的",
  },
};

const STORAGE_KEY = "madubau_cookie_consent";

export default function CookieBanner() {
  const { lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const t = COPY[lang] || COPY.en;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const set = (choice) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, at: new Date().toISOString() })
    );
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-8 md:right-auto md:max-w-md z-[150] border border-gold/40 bg-black/95 backdrop-blur-md shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="p-5 pr-10">
            <button
              type="button"
              onClick={() => set("essential")}
              aria-label="Close"
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center text-cream/60 hover:text-gold"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="font-display text-[9px] tracking-[0.4em] text-gold mb-3">/ COOKIES</div>
            <p className="text-sm text-cream/80 font-body leading-relaxed mb-5">
              {t.body}{" "}
              <Link to="/datenschutz" className="text-gold hover:underline">
                {t.link}
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => set("all")}
                data-testid="cookie-accept-all"
                className="flex-1 min-w-[140px] bg-gold text-black font-display text-[10px] tracking-[0.3em] py-3 px-5 hover:bg-gold-light gold-shine"
                style={{ background: "#C9A24C" }}
              >
                → {t.accept}
              </button>
              <button
                type="button"
                onClick={() => set("essential")}
                data-testid="cookie-essential"
                className="flex-1 min-w-[140px] border border-white/20 text-cream/80 hover:border-gold hover:text-gold font-display text-[10px] tracking-[0.3em] py-3 px-5 transition-colors"
              >
                {t.essential}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
